import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Error from "next/error";
import FlowPage, { flowApi } from "../index";
import { Flow, FinancialBrand } from "@dreambigger/shared/src/types";
import { HeadProps, Spin } from "@dreambigger/design-system";
import { FinancialInstitutionApi } from "../../../../api";
import { api } from "@dreambigger/shared/src/api/acquire";

// This is a client-side only route for variants
// No SSR required, so we can fetch everything on the client

// Initialize the financial institution API
const financialInstitutionApi = new FinancialInstitutionApi(api);

interface VariantData {
  id: string;
  slug: string;
  title: string;
  status: string;
  brand?: FinancialBrand;
  head?: HeadProps;
}

const VariantFlowPage = () => {
  const router = useRouter();
  const { flowId, brandVariantId } = router.query;
  const [flowData, setFlowData] = useState<Flow | null>(null);
  const [variantData, setVariantData] = useState<VariantData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasTimeout, setHasTimeout] = useState(false);

  useEffect(() => {
    if (!flowId || !brandVariantId) return;

    // Set a timeout to handle configuration errors
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setHasTimeout(true);
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Fetch flow data
        const flowRes = await flowApi.getById(flowId as string);
        const flow = flowRes.data;

        if (flow.status !== "active") {
          setNotFound(true);
          return;
        }

        // Fetch variant data using financial institution ID from flow
        const variantRes = await financialInstitutionApi.getBrandVariant(
          flow.financialInstitution.id,
          brandVariantId as string
        );
        const variant = variantRes.data as VariantData;

        setVariantData(variant);
        setFlowData(flow);
      } catch (err: any) {
        console.error("Failed to load brand variant:", err);
        // Check if it's a 404 error
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          // For other errors, redirect to base flow
          router.push(`/flows/${flowId}`);
        }
      } finally {
        clearTimeout(timeoutId);
        setIsLoading(false);
      }
    };

    fetchData();

    return () => clearTimeout(timeoutId);
  }, [flowId, brandVariantId]);

  // Show 404 error if brand variant not found
  if (notFound) {
    return <Error statusCode={404} />;
  }

  // Show error if configuration timeout occurred
  if (hasTimeout) {
    return (
      <Error statusCode={400} title="Configuration error: Request timed out" />
    );
  }

  if (isLoading || !flowData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Merge the variant brand and head with the base skin to preserve theme and other properties
  // The variant may override certain properties like colors or logos, but will inherit any properties not defined in the variant.
  const baseSkin = flowData.skins[0];
  const mergedBrand = variantData?.brand
    ? {
        ...baseSkin.brand,
        ...variantData.brand,
      }
    : undefined;
  const mergedHead = variantData?.head
    ? {
        ...baseSkin.head,
        ...variantData.head,
      }
    : undefined;

  return <FlowPage flowData={flowData} brand={mergedBrand} head={mergedHead} />;
};

export default VariantFlowPage;
